/**
 * Wedding Party + Guest Profile + Public Feed pages — Iter-21.
 *
 * Exports:
 *   GuestProfilePage        — /dashboard/guests/:guestId — full editable profile
 *   WeddingPartyPage        — /dashboard/wedding-party — list + click → profile
 *   PublicFeedTab           — Couple Settings tab content for opt-in toggle
 *   PublicFeedPage          — /feed — public marketplace of opted-in weddings
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Heart, Crown, Sparkles, Search, Globe, Lock, ExternalLink,
  Shirt, MapPin, Calendar, MessageCircle, Image as ImageIcon, Users, Star, Camera,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import { VideoNotesCard } from "../../components/site/FeedTools";
import { AttireCard } from "../../components/party/AttireCard";
import { PartyPhotoUpload } from "../../components/party/PartyPhotoUpload";

const PARTY_ROLES = [
  { v: "bride", l: "Bride" },
  { v: "groom", l: "Groom" },
  { v: "maid_of_honor", l: "Maid of Honor" },
  { v: "matron_of_honor", l: "Matron of Honor" },
  { v: "best_man", l: "Best Man" },
  { v: "bridesmaid", l: "Bridesmaid" },
  { v: "groomsman", l: "Groomsman" },
  { v: "flower_girl", l: "Flower Girl" },
  { v: "ring_bearer", l: "Ring Bearer" },
  { v: "parent", l: "Parent" },
  { v: "officiant", l: "Officiant" },
  { v: "usher", l: "Usher" },
  { v: "other", l: "Other" },
];

const TRAVEL_STATUSES = [
  { v: "unknown", l: "Unknown" },
  { v: "local", l: "Local" },
  { v: "flying", l: "Flying" },
  { v: "driving", l: "Driving" },
  { v: "booked", l: "Travel booked" },
];

// ============== GUEST PROFILE ==============
export function GuestProfilePage() {
  const { guestId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState({});
  const [coreEdit, setCoreEdit] = useState({});
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/guests/${guestId}`);
      setData(data);
      const g = data.guest;
      setProfile({
        is_wedding_party: !!g.is_wedding_party,
        wedding_party_role: g.wedding_party_role || "",
        photo_url: g.photo_url || "",
        pronouns: g.pronouns || "",
        dietary: g.dietary || "",
        allergies: g.allergies || "",
        bio: g.bio || "",
        instagram: g.instagram || "",
        relationship: g.relationship || "",
        travel_status: g.travel_status || "unknown",
        arrival_date: g.arrival_date || "",
        departure_date: g.departure_date || "",
      });
      setCoreEdit({
        name: g.name || "",
        email: g.email || "",
        phone: g.phone || "",
        group: g.group || "Family",
        meal_preference: g.meal_preference || "",
        notes: g.notes || "",
        rsvp_status: g.rsvp_status || "pending",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => { load(); }, [guestId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveCore() {
    setBusy(true);
    try {
      await api.patch(`/guests/${guestId}`, coreEdit);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function saveProfile() {
    setBusy(true);
    try {
      const payload = { ...profile };
      // Empty strings for optional date fields cause Pydantic to reject — strip
      ["arrival_date", "departure_date"].forEach((k) => { if (!payload[k]) delete payload[k]; });
      if (!payload.wedding_party_role) delete payload.wedding_party_role;
      await api.patch(`/guests/${guestId}/profile`, payload);
      toast.success("Profile saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function uploadPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image");
    if (f.size > 10 * 1024 * 1024) return toast.error("Photo must be under 10MB");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data: up } = await api.post(`/guests/${guestId}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProfile((p) => ({ ...p, photo_url: up.photo_url }));
      toast.success("Photo uploaded");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); e.target.value = ""; }
  }

  if (!data) {
    return <div className="text-center py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  }
  const { guest, outfits, memories_count, wedding } = data;

  return (
    <div className="max-w-4xl" data-testid="guest-profile-page">
      <button onClick={() => nav(-1)} className="text-xs text-[var(--muted)] flex items-center gap-1 mb-6 hover:text-[var(--gold)]">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      <div className="flex items-start gap-6 mb-8">
        <div className="w-24 h-24 rounded-full bg-[var(--bg-warm)] flex items-center justify-center overflow-hidden shrink-0">
          {profile.photo_url
            ? <img src={profile.photo_url} alt="" className="w-full h-full object-cover" />
            : <span className="font-serif text-4xl text-[var(--gold)]">{(guest.name || "?")[0]?.toUpperCase()}</span>}
        </div>
        <div className="flex-1">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2 flex items-center gap-2">
            {guest.is_wedding_party && <Crown className="w-3 h-3" />}
            {guest.is_wedding_party ? `Wedding Party · ${PARTY_ROLES.find((r) => r.v === guest.wedding_party_role)?.l || ""}` : "Guest"}
          </p>
          <h1 className="font-serif font-light text-4xl">{guest.name}</h1>
          <p className="text-[var(--muted)] mt-1 text-sm">
            {guest.group || "Guest"} · RSVP {guest.rsvp_status || "pending"}
            {profile.pronouns && <> · {profile.pronouns}</>}
          </p>
        </div>
      </div>

      {/* Core fields */}
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 mb-6">
        <h2 className="font-serif text-xl mb-4">Core info</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Name"><input value={coreEdit.name} onChange={(e) => setCoreEdit({ ...coreEdit, name: e.target.value })} className="input-luxe" data-testid="guest-name" /></Field>
          <Field label="Group"><input value={coreEdit.group} onChange={(e) => setCoreEdit({ ...coreEdit, group: e.target.value })} className="input-luxe" /></Field>
          <Field label="Email"><input value={coreEdit.email} onChange={(e) => setCoreEdit({ ...coreEdit, email: e.target.value })} className="input-luxe" /></Field>
          <Field label="Phone"><input value={coreEdit.phone} onChange={(e) => setCoreEdit({ ...coreEdit, phone: e.target.value })} className="input-luxe" /></Field>
          <Field label="RSVP">
            <select value={coreEdit.rsvp_status} onChange={(e) => setCoreEdit({ ...coreEdit, rsvp_status: e.target.value })} className="input-luxe">
              <option value="pending">Pending</option>
              <option value="attending">Attending</option>
              <option value="not_attending">Not attending</option>
              <option value="maybe">Maybe</option>
            </select>
          </Field>
          <Field label="Meal preference"><input value={coreEdit.meal_preference} onChange={(e) => setCoreEdit({ ...coreEdit, meal_preference: e.target.value })} className="input-luxe" /></Field>
          <div className="md:col-span-2">
            <Field label="Notes"><textarea value={coreEdit.notes} onChange={(e) => setCoreEdit({ ...coreEdit, notes: e.target.value })} rows={2} className="input-luxe resize-none" /></Field>
          </div>
        </div>
        <button onClick={saveCore} disabled={busy} data-testid="save-core" className="btn-gold mt-5"><Save className="w-4 h-4" /> Save core info</button>
      </div>

      {/* Wedding party + extended profile */}
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 mb-6">
        <h2 className="font-serif text-xl mb-4">Wedding party & profile</h2>
        <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={profile.is_wedding_party}
            onChange={(e) => setProfile({ ...profile, is_wedding_party: e.target.checked })}
            data-testid="toggle-wedding-party"
          />
          <span className="text-sm">This guest is part of our wedding party</span>
        </label>
        {profile.is_wedding_party && (
          <Field label="Role">
            <select value={profile.wedding_party_role || ""} onChange={(e) => setProfile({ ...profile, wedding_party_role: e.target.value })} className="input-luxe" data-testid="wedding-party-role">
              <option value="">Choose a role…</option>
              {PARTY_ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </Field>
        )}
        <div className="grid md:grid-cols-2 gap-4 mt-2">
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block mb-2">Profile photo</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-[var(--bg-warm)] flex items-center justify-center overflow-hidden border border-[var(--line)]">
                {profile.photo_url
                  ? <img src={profile.photo_url.startsWith("/") ? `${BACKEND_URL}${profile.photo_url}` : profile.photo_url} alt="" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
                  : <Camera className="w-6 h-6 text-[var(--muted)]" />}
              </div>
              <div className="flex-1 space-y-2">
                <label className="btn-outline-gold text-xs cursor-pointer inline-flex" data-testid="upload-photo-button">
                  <Camera className="w-3 h-3" /> Upload photo
                  <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="upload-photo-input" />
                </label>
                <input
                  value={profile.photo_url || ""}
                  onChange={(e) => setProfile({ ...profile, photo_url: e.target.value })}
                  className="input-luxe text-xs"
                  placeholder="…or paste an image URL"
                  data-testid="photo-url-input"
                />
              </div>
            </div>
          </div>
          <Field label="Pronouns"><input value={profile.pronouns} onChange={(e) => setProfile({ ...profile, pronouns: e.target.value })} className="input-luxe" placeholder="she/her, he/him, they/them" /></Field>
          <Field label="Relationship"><input value={profile.relationship} onChange={(e) => setProfile({ ...profile, relationship: e.target.value })} className="input-luxe" placeholder="bride's sister · college friend · …" /></Field>
          <Field label="Instagram"><input value={profile.instagram} onChange={(e) => setProfile({ ...profile, instagram: e.target.value })} className="input-luxe" placeholder="@handle" /></Field>
          <Field label="Dietary preference"><input value={profile.dietary} onChange={(e) => setProfile({ ...profile, dietary: e.target.value })} className="input-luxe" placeholder="vegan · halal · …" /></Field>
          <Field label="Allergies"><input value={profile.allergies} onChange={(e) => setProfile({ ...profile, allergies: e.target.value })} className="input-luxe" placeholder="nuts, shellfish, …" /></Field>
          <Field label="Travel status">
            <select value={profile.travel_status} onChange={(e) => setProfile({ ...profile, travel_status: e.target.value })} className="input-luxe">
              {TRAVEL_STATUSES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field label="Arrival date"><input type="date" value={profile.arrival_date || ""} onChange={(e) => setProfile({ ...profile, arrival_date: e.target.value })} className="input-luxe" /></Field>
          <Field label="Departure date"><input type="date" value={profile.departure_date || ""} onChange={(e) => setProfile({ ...profile, departure_date: e.target.value })} className="input-luxe" /></Field>
          <div className="md:col-span-2">
            <Field label="Bio (shown on shareable profile)"><textarea value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value.slice(0, 2000) })} rows={3} className="input-luxe resize-none" placeholder="A few sentences about this special person." /></Field>
          </div>
        </div>
        <button onClick={saveProfile} disabled={busy} data-testid="save-profile" className="btn-gold mt-5"><Save className="w-4 h-4" /> Save profile</button>
      </div>

      {/* Linked outfits */}
      <div className="bg-white border border-[var(--line)] rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-xl flex items-center gap-2"><Shirt className="w-4 h-4 text-[var(--gold)]" /> Outfits</h2>
          <Link to="/dashboard/dress-tux" className="text-xs text-[var(--gold)] hover:underline">Manage in Dress & Tux Tracker →</Link>
        </div>
        {outfits.length === 0 ? (
          <p className="text-[var(--muted)] text-sm">No outfit linked yet. Add one in <Link to="/dashboard/dress-tux" className="text-[var(--gold)] underline">Dress & Tux Tracker</Link> with member name "{guest.name}".</p>
        ) : (
          <div className="space-y-2">
            {outfits.map((o) => (
              <div key={o.outfit_id} className="flex items-center justify-between border-b border-[var(--line)]/60 pb-2 text-sm" data-testid={`linked-outfit-${o.outfit_id}`}>
                <div>
                  <p className="font-medium">{o.garment_type} · {o.role}</p>
                  <p className="text-xs text-[var(--muted)]">Size {o.size || "—"} · {o.color || "—"} · {o.vendor || "—"}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase ${
                  o.payment_status === "paid" ? "bg-emerald-50 text-emerald-600"
                  : o.payment_status === "deposit" ? "bg-amber-50 text-amber-600"
                  : "bg-[var(--bg-warm)] text-[var(--muted)]"
                }`}>{o.payment_status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick stats / links */}
      <div className="grid md:grid-cols-3 gap-4">
        <StatLink to={`/site/${wedding.slug}/rsvp/${guest.guest_id}`} icon={MessageCircle} label="Public RSVP link" testid="link-rsvp" />
        <StatLink to="/dashboard/memories" icon={ImageIcon} label={`${memories_count} memories uploaded`} testid="link-memories" />
        <StatLink to="/dashboard/seating" icon={Users} label={`Table: ${guest.table || "unassigned"}`} testid="link-seating" />
      </div>

      {/* Public mini-page (only if wedding party) */}
      {guest.is_wedding_party && (
        <div className="mt-6 bg-gradient-to-br from-[var(--ink)] to-[#111111]/90 text-white rounded-2xl p-6 md:p-8" data-testid="mini-page-card">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2 flex items-center gap-1"><Crown className="w-3 h-3" /> Locked In Roster</p>
          <h3 className="font-serif text-2xl mb-2">{guest.name?.split(" ")[0]}'s public mini-page is live</h3>
          <p className="text-white/70 text-sm mb-4">Share this link on Instagram bio or in invites. Visitors see {guest.name?.split(" ")[0]}'s photo, role, bio, and outfit teaser — and they can leave a guest-book note.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link to={`/site/${wedding.slug}/party/${guest.guest_id}`} target="_blank" className="btn-gold text-xs justify-center" data-testid="open-mini-page">
              Open mini-page →
            </Link>
            <button
              onClick={() => {
                const url = `${window.location.origin}/site/${wedding.slug}/party/${guest.guest_id}`;
                navigator.clipboard?.writeText(url);
                toast.success("Link copied!");
              }}
              data-testid="copy-mini-link"
              className="btn-outline-gold text-xs justify-center"
            >
              Copy link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block mb-1">{label}</label>
      {children}
    </div>
  );
}

function StatLink({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} className="bg-white border border-[var(--line)] rounded-2xl p-4 hover:border-[var(--gold)] transition flex items-center gap-3" data-testid={testid}>
      <Icon className="w-5 h-5 text-[var(--gold)]" />
      <span className="text-sm">{label}</span>
    </Link>
  );
}

// ============== WEDDING PARTY ==============
export function WeddingPartyPage() {
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(true);

  async function load() {
    setBusy(true);
    try { const { data } = await api.get("/weddings/me/wedding-party"); setMembers(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="wedding-party-page">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2 flex items-center gap-2"><Crown className="w-3 h-3" /> Inner circle</p>
        <h1 className="font-serif font-light text-4xl">Our Wedding Party</h1>
        <p className="text-[var(--muted)] mt-2 text-sm">Click any member to edit their profile, sizes, dietary needs, and travel.</p>
      </div>

      {busy ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : members.length === 0 ? (
        <div className="bg-white border border-[var(--line)] rounded-2xl p-10 text-center">
          <Heart className="w-8 h-8 text-[var(--gold)] mx-auto mb-4" />
          <p className="font-serif text-2xl mb-2">Build your circle</p>
          <p className="text-[var(--muted)] text-sm mb-6">Mark guests as wedding party from their profile.</p>
          <Link to="/dashboard/guests" className="btn-gold">Open guest list</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {members.map((m) => (
            <Link
              key={m.guest_id}
              to={`/dashboard/guests/${m.guest_id}`}
              data-testid={`party-card-${m.guest_id}`}
              className="bg-white border border-[var(--line)] rounded-2xl p-5 hover:border-[var(--gold)] hover:shadow-md transition"
            >
              <div className="mb-4">
                <PartyPhotoUpload endpoint={`/weddings/me/wedding-party/${m.guest_id}/photo`} name={m.name} photo={m.photo_url} testid={`party-photo-${m.guest_id}`}
                  onDone={(url) => setMembers((xs) => xs.map((x) => (x.guest_id === m.guest_id ? { ...x, photo_url: url } : x)))} />
              </div>
              <p className="font-serif text-lg">{m.name}</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">
                {PARTY_ROLES.find((r) => r.v === m.wedding_party_role)?.l || "Wedding party"}
              </p>
              <div className="mt-3 flex gap-2 flex-wrap">
                {m.outfits?.length > 0 && (
                  <span className="text-[10px] px-2 py-0.5 bg-[var(--bg-warm)] rounded-full">
                    <Shirt className="w-3 h-3 inline" /> {m.outfits.length} outfit{m.outfits.length > 1 ? "s" : ""}
                  </span>
                )}
                {m.dietary && <span className="text-[10px] px-2 py-0.5 bg-[var(--bg-warm)] rounded-full">{m.dietary}</span>}
                {m.travel_status && m.travel_status !== "unknown" && (
                  <span className="text-[10px] px-2 py-0.5 bg-[var(--bg-warm)] rounded-full capitalize">{m.travel_status}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
      <AttireCard />
      <VideoNotesCard />
    </div>
  );
}

// ============== PUBLIC FEED OPT-IN (settings tab) ==============
export function PublicFeedTab() {
  const [s, setS] = useState({ is_public_feed: false, feed_blurb: "", is_featured_on_feed: false });
  const [busy, setBusy] = useState(false);

  async function load() {
    try { const { data } = await api.get("/weddings/me/feed"); setS(data); }
    catch {}
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.patch("/weddings/me/feed", { is_public_feed: s.is_public_feed, feed_blurb: s.feed_blurb || "", hero_opt_in: !!s.hero_opt_in });
      toast.success(data.is_public_feed ? "You're in the public feed!" : "Hidden from feed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="bg-white border border-[var(--line)] rounded-2xl p-6 md:p-8" data-testid="public-feed-tab">
      <div className="flex items-center gap-3 mb-2">
        {s.is_public_feed ? <Globe className="w-5 h-5 text-[var(--gold)]" /> : <Lock className="w-5 h-5 text-[var(--muted)]" />}
        <h2 className="font-serif text-2xl">Public Feed</h2>
        {s.is_featured_on_feed && (
          <span className="text-[10px] px-2 py-0.5 bg-[#C8A96B]/15 text-[var(--gold)] rounded-full uppercase tracking-[0.18em] flex items-center gap-1">
            <Star className="w-3 h-3" /> Featured by editors
          </span>
        )}
      </div>
      <p className="text-[var(--muted)] text-sm mb-6">
        Couples in our public feed at <Link to="/feed" className="text-[var(--gold)] underline">lockedinforever.com/feed</Link> get discovered by other couples planning their own day —
        and editors may feature your story on the homepage. You can toggle this off at any time.
      </p>

      <label className="flex items-center gap-3 mb-5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={s.is_public_feed}
          onChange={(e) => setS({ ...s, is_public_feed: e.target.checked })}
          data-testid="feed-toggle"
        />
        <span className="text-sm">Show our wedding in the public feed & search</span>
      </label>

      {s.is_public_feed && (
        <label className="flex items-start gap-3 mb-5 cursor-pointer select-none" data-testid="hero-opt-in-row">
          <input type="checkbox" checked={!!s.hero_opt_in} onChange={(e) => setS({ ...s, hero_opt_in: e.target.checked })} className="mt-1" data-testid="hero-opt-in-toggle" />
          <span className="text-sm">Feature us on the homepage
            <span className="block text-xs text-[var(--muted)]">Couples who tick this go first when we pick the 3 weddings shown on the homepage each day.</span>
            {s.hero_featured_at && <span className="block text-xs text-[var(--gold)] mt-1" data-testid="hero-featured-since">You were featured on {new Date(s.hero_featured_at).toLocaleDateString()}. Your site shows the gold badge.</span>}
          </span>
        </label>
      )}

      {s.is_public_feed && (
        <Field label={`Feed blurb (${(s.feed_blurb || "").length}/280) — optional, shown under your couple name`}>
          <textarea
            value={s.feed_blurb || ""}
            onChange={(e) => setS({ ...s, feed_blurb: e.target.value.slice(0, 280) })}
            rows={3}
            className="input-luxe resize-none"
            placeholder="Atlanta · July 2026 · A black-tie love story…"
            data-testid="feed-blurb"
          />
        </Field>
      )}

      <button onClick={save} disabled={busy} className="btn-gold mt-5" data-testid="save-feed-settings">
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

// ============== PUBLIC FEED PAGE ==============
export function PublicFeedPage() {
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);

  async function load() {
    setBusy(true);
    try {
      const params = q.trim() ? { q: q.trim() } : {};
      const [{ data: feed }, { data: feat }] = await Promise.all([
        api.get("/public/feed", { params }),
        api.get("/public/feed/featured"),
      ]);
      setItems(feed.items || []);
      setFeatured(feat || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="public-feed-page">
      <div className="bg-[var(--ink)] text-white py-20 px-6">
        <div className="container-wide max-w-5xl text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3">All weddings · The Feed</p>
          <h1 className="font-serif font-light text-5xl md:text-6xl mb-6">Real love stories.<br />Locked in forever.</h1>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Browse weddings around the world. Get inspired. Each couple opted-in to share their story with you.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="max-w-xl mx-auto flex gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by city, couple, hashtag…"
                data-testid="feed-search"
                className="w-full bg-white/10 border border-white/20 rounded-full pl-11 pr-4 py-3 text-white placeholder-white/50 focus:outline-none focus:border-[var(--gold)]"
              />
            </div>
            <button type="submit" data-testid="feed-search-btn" className="btn-gold">Search</button>
          </form>
        </div>
      </div>

      {featured.length > 0 && (
        <section className="container-wide max-w-6xl mx-auto px-6 py-12">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 text-[var(--gold)]" />
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">Editors' picks</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {featured.map((w) => (
              <Link key={w.slug} to={`/site/${w.slug}`} data-testid={`featured-${w.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-[var(--line)] hover:border-[var(--gold)] transition">
                <div className="aspect-[4/3] bg-[var(--bg-warm)] overflow-hidden">
                  {w.cover_image && <img src={w.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" />}
                </div>
                <div className="p-5">
                  <p className="font-script text-[var(--gold)] text-2xl">{w.couple_names}</p>
                  {w.feed_blurb && <p className="text-sm text-[var(--muted)] mt-1 line-clamp-2">{w.feed_blurb}</p>}
                  <div className="flex items-center gap-3 mt-3 text-xs text-[var(--muted)]">
                    {w.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {w.city}</span>}
                    {w.wedding_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {(w.wedding_date || "").slice(0, 10)}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="container-wide max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-3xl">All weddings</h2>
          <p className="text-xs text-[var(--muted)]">{items.length} weddings · most recent first</p>
        </div>
        {busy ? (
          <div className="text-center py-20"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-[var(--muted)]">No weddings match your search yet. <Link to="/signup" className="text-[var(--gold)] underline">Be the first.</Link></div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((w) => (
              <Link
                key={w.slug}
                to={`/site/${w.slug}`}
                data-testid={`feed-card-${w.slug}`}
                className="group bg-white rounded-2xl overflow-hidden border border-[var(--line)] hover:border-[var(--gold)] transition"
              >
                <div className="aspect-square bg-[var(--bg-warm)] overflow-hidden relative">
                  {w.cover_image && <img src={w.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition" />}
                  {w.is_featured_on_feed && (
                    <span className="absolute top-2 right-2 bg-[var(--gold)] text-white text-[9px] px-2 py-0.5 rounded-full uppercase tracking-[0.16em] flex items-center gap-1">
                      <Star className="w-2.5 h-2.5" /> Featured
                    </span>
                  )}
                  {w.is_demo && (
                    <span className="absolute bottom-2 left-2 bg-white/90 text-[var(--ink)] text-[9px] px-2 py-0.5 rounded-full uppercase tracking-[0.16em]">Demo</span>
                  )}
                </div>
                <div className="p-4">
                  <p className="font-script text-[var(--gold)] text-xl truncate">{w.couple_names}</p>
                  {w.feed_blurb && <p className="text-xs text-[var(--muted)] mt-1 line-clamp-2">{w.feed_blurb}</p>}
                  <div className="flex items-center gap-2 mt-2 text-[10px] text-[var(--muted)]">
                    {w.city && <span>{w.city}</span>}
                    {w.wedding_date && <span>· {(w.wedding_date || "").slice(0, 7)}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
